import { useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

/**
 * Custom hook to handle mobile-specific setup for Capacitor
 * Initializes plugins and provides mobile utility functions
 */
const useMobileSetup = () => {
  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'

  // Initialize mobile setup on mount
  useEffect(() => {
    const initMobile = async () => {
      if (!isNative) return;

      try {
        // Configure status bar (dark content for light backgrounds, or vice versa)
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#1e293b' });

        // Lock to landscape for game experience
        await ScreenOrientation.lock({ orientation: 'landscape' });

        // Hide splash screen after a short delay
        setTimeout(async () => {
          await SplashScreen.hide();
        }, 500);
      } catch (error) {
        console.log('Mobile setup error:', error);
      }
    };

    initMobile();

    // Cleanup: unlock orientation when component unmounts
    return () => {
      if (isNative) {
        ScreenOrientation.unlock().catch(() => {});
      }
    };
  }, [isNative]);

  // Haptic feedback for interactions
  const triggerHaptic = useCallback(async (style = 'medium') => {
    if (!isNative) return;

    try {
      const impactStyles = {
        light: ImpactStyle.Light,
        medium: ImpactStyle.Medium,
        heavy: ImpactStyle.Heavy
      };
      await Haptics.impact({ style: impactStyles[style] || ImpactStyle.Medium });
    } catch (error) {
      console.log('Haptic error:', error);
    }
  }, [isNative]);

  // Vibrate for notifications/alerts
  const triggerVibrate = useCallback(async () => {
    if (!isNative) return;

    try {
      await Haptics.vibrate();
    } catch (error) {
      console.log('Vibrate error:', error);
    }
  }, [isNative]);

  // Lock/unlock screen orientation
  const lockOrientation = useCallback(async (orientation = 'landscape') => {
    if (!isNative) return;

    try {
      await ScreenOrientation.lock({ orientation });
    } catch (error) {
      console.log('Orientation lock error:', error);
    }
  }, [isNative]);

  const unlockOrientation = useCallback(async () => {
    if (!isNative) return;

    try {
      await ScreenOrientation.unlock();
    } catch (error) {
      console.log('Orientation unlock error:', error);
    }
  }, [isNative]);

  // Show/hide status bar
  const showStatusBar = useCallback(async () => {
    if (!isNative) return;

    try {
      await StatusBar.show();
    } catch (error) {
      console.log('StatusBar show error:', error);
    }
  }, [isNative]);

  const hideStatusBar = useCallback(async () => {
    if (!isNative) return;

    try {
      await StatusBar.hide();
    } catch (error) {
      console.log('StatusBar hide error:', error);
    }
  }, [isNative]);

  return {
    isNative,
    platform,
    triggerHaptic,
    triggerVibrate,
    lockOrientation,
    unlockOrientation,
    showStatusBar,
    hideStatusBar
  };
};

export default useMobileSetup;
